import apiClient from '@/services/api'
import moment from 'moment-timezone'
import { prepareFilterWithEmployeeLegalEntities } from '@/utils/payment-type-legal-entity-filter'
import { normalizeLiveStatusFilters } from '@/modules/profile/utils/live-status-filters'

const normalizeUnverifiedFilter = value => {
  if (value === true || value === 'true') {
    return true
  }

  if (value === false || value === 'false') {
    return false
  }

  return undefined
}

export default {
  async getAssociates({ rootState }, { filter, page, size }) {
    const currentPaymentType = rootState.applicationState.currentPaymentTypeName
    const manager_scopes = rootState.applicationState.associate.manager_scopes
    const paymentTypes =
      currentPaymentType === 'All' ? (filter.payment_types ?? filter.payment_type) : [currentPaymentType.toLowerCase()]
    const normalizedFilter = prepareFilterWithEmployeeLegalEntities({
      filter,
      paymentTypeKey: 'payment_types',
      legalEntityKey: 'legal_entities',
      paymentTypes
    })

    try {
      const { data } = await apiClient.post(
        '/api/associate/get_all?fields={-permissions,-write_up_count,-is_system,-schedule_type}',
        {
          project_id: rootState.applicationState.currentProject.id,
          page,
          size,
          filter: {
            ...normalizedFilter,
            unverified: normalizeUnverifiedFilter(filter.unverified),
            manager_scopes,
            payment_type: currentPaymentType === 'All' ? filter.payment_type : currentPaymentType
          }
        },
        {
          cancelPreviousRequests: true
        }
      )

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async getAssociatesActivity({ rootState }, { filter, page, size, ordering }) {
    const currentPaymentType = rootState.applicationState.currentPaymentTypeName
    const currentPaymentTypeId = rootState.applicationState.currentPaymentType
    const filters = normalizeLiveStatusFilters({
      ...filter,
      payment_type: currentPaymentType === 'All' ? filter.payment_type : currentPaymentTypeId
    })

    try {
      const { data } = await apiClient.get(
        `/api/dashboard/project/${rootState.applicationState.currentProject.id}/live_status/details_v2`,
        {
          params: { ...filters, page, size, ...(ordering?.length ? { ordering: ordering.join(',') } : {}) },
          cancelPreviousRequests: true
        }
      )

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async getLiveShiftAssignments(_, { shiftId }) {
    try {
      const { data } = await apiClient.get(`/api/shifts/${shiftId}/assignments`)
      return data
    } catch (error) {
      throw error.message
    }
  },

  async exportAssociatesActivity({ rootState }, { delivery, email, filter, ordering, idempotencyKey }) {
    const payment_type =
      rootState.applicationState.currentPaymentTypeName === 'All'
        ? filter.payment_type
        : rootState.applicationState.currentPaymentType
    const filters = Object.fromEntries(
      Object.entries({ ...filter, payment_type }).filter(
        ([, value]) => value !== undefined && value !== null && value !== '' && (!Array.isArray(value) || value.length)
      )
    )
    const payload = { delivery, filters, ...(ordering?.length ? { ordering } : {}), ...(email ? { email } : {}) }
    try {
      const response = await apiClient.post(
        `/api/dashboard/project/${rootState.applicationState.currentProject.id}/live_status/details_v2/export`,
        payload,
        {
          responseType: delivery === 'download' ? 'blob' : 'json',
          headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined
        }
      )
      return response.data
    } catch (error) {
      throw error.message
    }
  },

  setAssociate({ commit }, payload) {
    commit('SET_ASSOCIATE', payload)
  },
  async fetchResponsibleContacts(_, { associateId }) {
    try {
      const { data } = await apiClient.get(`/api/associate/${associateId}/responsible_contacts`)
      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
      return data?.contacts?.administrator ?? []
    } catch (error) {
      throw error.message
    }
  },
  async getAssociate({ commit, rootState }, { associate_number, project_id }) {
    try {
      const { data } = await apiClient.post('/api/associate/get', {
        project_id: project_id ?? rootState.applicationState.currentProject.id,
        associate_number
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while loading the associate.'}`)
      }

      commit('SET_ASSOCIATE', data.associate)
    } catch (error) {
      throw error.message
    }
  },

  async getAssociatesById({ rootState }, { associates, associate_numbers }) {
    try {
      const { data } = await apiClient.post('/api/associate/search', {
        project_id: rootState.applicationState.currentProject.id,
        limit: 1000,
        filter: {
          associates,
          associate_numbers
        }
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.associates
    } catch (error) {
      throw error.message
    }
  },

  setAssociateCompliance({ commit }, payload) {
    commit('SET_ASSOCIATE_COMPLIANCE', payload)
  },

  setAssociateCertification({ commit }, payload) {
    commit('SET_ASSOCIATE_CERTIFICATION', payload)
  },

  setAssociateCompanyFlow({ commit }, payload) {
    commit('SET_ASSOCIATE_COMPANY_FLOW', payload)
  },

  setAssociateLifecycleEvents({ commit }, payload) {
    commit('SET_ASSOCIATE_LIFECYCLE_EVENTS', payload)
  },

  async getProfileHistory({ commit }, { id }) {
    try {
      const { data } = await apiClient.get('/api/associate/change_history', {
        params: { id }
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      commit('SET_ASSOCIATE_CHANGES_HISTORY', data.history)
    } catch (error) {
      throw error.message
    }
  },

  async getProfileNewHistoryEventTypes() {
    const { data } = await apiClient.get('/api/associate/new_change_history/event_types')

    if (data.error_code && data.error_code !== '0') {
      throw new Error(`${data.msg}`)
    }

    return data.event_types ?? []
  },

  async getProfileNewHistory(_, { id, params }) {
    const { data } = await apiClient.get(`/api/associate/${id}/new_change_history`, {
      params
    })

    if (data.error_code && data.error_code !== '0') {
      throw new Error(`${data.msg}`)
    }

    return data.change_history ?? []
  },

  async getAssociateCompliance({ commit, state }, { associate_id }) {
    commit('SET_ASSOCIATE_COMPLIANCE', undefined)
    try {
      const { data } = await apiClient.get('/api/associate/documentation_compliance', {
        params: { associate_id }
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      if (state.associate?.id === associate_id) {
        commit('SET_ASSOCIATE_COMPLIANCE', data.documentation_compliance)
      }
    } catch (error) {
      throw error.message
    }
  },

  async getAssociateCertification({ commit }, { associate_id }) {
    try {
      const { data } = await apiClient.get('/api/associate/epeople_certification', {
        params: { associate_id }
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(data.msg)
      }

      commit('SET_ASSOCIATE_CERTIFICATION', data.documentation_compliance)
    } catch (error) {
      throw error.message
    }
  },

  async getAssociateCompanyFlow({ commit }, { associate_id }) {
    try {
      const { data } = await apiClient.get(`/api/associate/${associate_id}/company_flow`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(data.msg)
      }

      commit('SET_ASSOCIATE_COMPANY_FLOW', data)

      return data
    } catch (error) {
      throw error.message
    }
  },

  async getAssociateLifecycleEvents({ commit }, { associate_id }) {
    try {
      const { data } = await apiClient.get(`/api/associate/${associate_id}/lifecycle_events`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(data.msg)
      }

      commit('SET_ASSOCIATE_LIFECYCLE_EVENTS', data)

      return data
    } catch (error) {
      throw error.message
    }
  },

  async getAssociateTrainingRenewal(_, { associate_id }) {
    try {
      const { data } = await apiClient.get(`/api/associate/${associate_id}/continuing_education_hours`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(data.msg)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async downloadAssociateTrainingRenewal(_, { associate_id }) {
    try {
      const { data } = await apiClient.get(`/api/associate/${associate_id}/continuing_education_hours/download`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while downloading continuing education hours.'}`)
      }

      return data.url
    } catch (error) {
      throw error.message
    }
  },

  async getAssociateMainInfo({ rootState }, { associate_number }) {
    try {
      const { data } = await apiClient.post('/api/associate/get/main_info', {
        project_id: rootState.applicationState.currentProject.id,
        associate_number
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while loading the associate.'}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async reloadAssociate({ commit }, flag) {
    commit('setRefetchAssociate', flag)
  },

  async getAssociateById(_, { associate_id }) {
    try {
      const { data } = await apiClient.get(`/api/associate/${associate_id}`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while loading the associate.'}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async deleteAssociate(_, { associate_id }) {
    try {
      const { data } = await apiClient.delete(`/api/associate/${associate_id}`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while deleting the associate.'}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async getAssociateSchedule(_, { associate_id }) {
    try {
      const { data } = await apiClient.get(`/api/associate/${associate_id}/schedule`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while loading the schedule.'}`)
      }

      return data.schedule
    } catch (error) {
      throw error.message
    }
  },

  async postAssociateSchedule(_, { associate_id, schedule }) {
    try {
      const { data } = await apiClient.post(`/api/associate/${associate_id}/schedule`, {
        schedule
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while saving the schedule.'}`)
      }

      return data.schedule ?? schedule
    } catch (error) {
      throw error.message
    }
  },

  async getAssociateProfiles({ rootState }, { associate_number, with_disabled, project_id }) {
    try {
      const { data } = await apiClient.post('/api/associate/profiles/get/new', {
        project_id: project_id ?? rootState.applicationState.currentProject.id,
        associate_number,
        with_disabled
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? "An error occurred while loading the associate's profiles."}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async getKPI({ commit, rootState }, { associate_number, start, end }) {
    try {
      const { data } = await apiClient.post('/api/associate/kpi/get', {
        project_id: rootState.applicationState.currentProject.id,
        associate_number,
        start,
        end
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while loading the KPI.'}`)
      }

      commit('setAssociateKPI', data.kpi)
    } catch (error) {
      throw error.message
    }
  },

  async getAttendanceRate({ commit }, { id, start_date, end_date }) {
    try {
      const { data } = await apiClient.get(`/api/associate/attendance_rate/${start_date}/${end_date}`, {
        params: { id }
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while loading the KPI.'}`)
      }

      commit('setAssociateAttendanceRate', data.attendance_rate)
    } catch (error) {
      throw error.message
    }
  },

  async getWriteUps({ commit, rootState }, { associate_number, project_id }) {
    if (!associate_number) {
      commit('setAssociateWriteUps', [])
      return []
    }

    try {
      const { data } = await apiClient.post('/api/associate/writeup/get_all', {
        project_id: project_id ?? rootState.applicationState.currentProject.id,
        associate_number
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while loading the written warnings.'}`)
      }

      commit('setAssociateWriteUps', data.writeups)

      return data.writeups
    } catch (error) {
      throw error.message
    }
  },

  async getAssociateFacilities({ commit }, { associate_id, page, size }) {
    try {
      const { data } = await apiClient.get(
        `/api/associate/${associate_id}/outer_associate?fields={project{id,name},associate{associate_number},status}`,
        {
          params: { page, size }
        }
      )

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while loading the written warnings.'}`)
      }

      commit('SET_ASSOCIATE_FACILITIES', data)
    } catch (error) {
      throw error.message
    }
  },

  async postAssociateWriteUp(
    { rootState, state, commit },
    { writeup, associate_number, manager_scopes, signature_id }
  ) {
    try {
      const { data } = await apiClient.post('/api/associate/writeup/create', {
        project_id: rootState.applicationState.currentProject.id,
        signature_id,
        writeup: {
          ...writeup,
          author: rootState.applicationState.associate.id,
          consumer: {
            project_id: rootState.applicationState.currentProject.id,
            associate_number,
            manager_scopes
          },
          status: 'not_signed',
          authorization: null
        }
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      commit('setAssociateWriteUps', [...state.associateWriteUps, data.writeup])
    } catch (error) {
      throw error.message
    }
  },

  async postSignWriteUp({ rootState, state, commit }, { writeup_id, signature_id }) {
    try {
      const { data } = await apiClient.post('/api/associate/writeup/sign', {
        project_id: rootState.applicationState.currentProject.id,
        signature_id,
        writeup_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      commit(
        'setAssociateWriteUps',
        state.associateWriteUps.map(item => (item.id === writeup_id ? { ...item, status: 'signed' } : item))
      )
    } catch (error) {
      throw error.message
    }
  },

  async downloadAssociateWriteUps(_, { id }) {
    try {
      const { data } = await apiClient.get('/api/associate/writeup/download', {
        params: {
          id
        }
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.url
    } catch (error) {
      throw error.message
    }
  },

  async postCancelWriteUp({ commit, rootState, state }, { writeup }) {
    try {
      const { data } = await apiClient.post('/api/associate/writeup/cancel', {
        project_id: rootState.applicationState.currentProject.id,
        writeup: {
          id: writeup
        }
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while cancelling the written warning.'}`)
      }

      commit(
        'setAssociateWriteUps',
        state.associateWriteUps.filter(item => item.id !== writeup)
      )
    } catch (error) {
      throw error.message
    }
  },

  async patchAssociateActivate(_, { associate_id }) {
    try {
      const { data } = await apiClient.patch(`/api/associate/${associate_id}/activate`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      // TODO: server response
    } catch (error) {
      throw error.message
    }
  },

  async patchAssociateDeactivate(_, { associate_id }) {
    try {
      const { data } = await apiClient.patch(`/api/associate/${associate_id}/deactivate`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      // TODO: server response
    } catch (error) {
      throw error.message
    }
  },

  async postCancelFutureTermination({ commit, state }, { associate_id, job_id }) {
    try {
      const { data } = await apiClient.post(`/api/associate/${associate_id}/termination/job_cancel`, {
        job_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
      commit('SET_ASSOCIATE', {
        ...state.associate,
        resigned_date: null,
        termination_job: null
      })
    } catch (error) {
      throw error.message
    }
  },

  async postAssociateTerminate({ rootState, commit }, { associate_id, termination, user_id }) {
    try {
      const { data } = await apiClient.post('/api/associate/terminate', {
        project_id: rootState.applicationState.currentProject.id,
        associate_id,
        user_id,
        termination: {
          ...termination,
          author: rootState.applicationState.associate.id
        }
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },

  async postAssociateResign({ rootState }, { associate_number, termination }) {
    try {
      const { data } = await apiClient.post('/api/associate/resign', {
        project_id: rootState.applicationState.currentProject.id,
        associate_number,
        termination
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },

  async patchAssociateTermination(_, { associate_id, termination }) {
    const { data } = await apiClient.patch(`/api/associate/${associate_id}/termination`, termination)

    if (data.error_code && data.error_code !== '0') {
      throw new Error(`${data.msg}`)
    }

    return data.termination
  },

  async postAssociateSuspension({ rootState, state, commit }, { associate_number, suspension }) {
    try {
      const { data } = await apiClient.post('/api/associate/suspension/set', {
        project_id: rootState.applicationState.currentProject.id,
        associate_number,
        suspension
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      // TODO: receive associate from server
      commit('SET_ASSOCIATE', {
        ...state.associate,
        suspension: data.suspension,
        work_status: 'suspended'
      })
    } catch (error) {
      throw error.message
    }
  },

  async postAssociateReleaseSuspension({ rootState, state, commit }, { associate_number }) {
    try {
      const { data } = await apiClient.post('/api/associate/suspension/release', {
        associate_number,
        project_id: rootState.applicationState.currentProject.id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      // TODO: receive associate from server
      commit('SET_ASSOCIATE', {
        ...state.associate,
        suspension: null,
        work_status: 'active'
      })
    } catch (error) {
      throw error.message
    }
  },

  async createAssociate({ rootState }, { user_id, associate_info, payment_info }) {
    try {
      const { data } = await apiClient.post('/api/associate/user', {
        user_id,
        associate_info: {
          ...associate_info,
          project_id: rootState.applicationState.currentProject.id
        },
        payment_info
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async createUser({ rootState }, { user_info, associate_info, payment_info, creation_mode }) {
    try {
      const { data } = await apiClient.post('/api/associate/create', {
        user_info: {
          ...user_info,
          birth_date: user_info.birth_date ? moment(user_info.birth_date).utc().startOf('day').toISOString() : undefined
        },
        associate_info: {
          ...associate_info,
          project_id: rootState.applicationState.currentProject.id
        },
        payment_info,
        creation_mode
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async updateAssociate({ rootState }, { associate, updated_associate_number }) {
    const serverDateFormat = rootState.applicationState.serverDateFormat

    try {
      const { data } = await apiClient.post('/api/associate/set', {
        project_id: rootState.applicationState.currentProject.id,
        associate: {
          ...associate,
          birth_date: associate.birth_date
            ? moment(associate.birth_date).utc().startOf('day').format(serverDateFormat)
            : undefined,
          hire_date: associate.hire_date
            ? moment(associate.hire_date).utc().startOf('day').format(serverDateFormat)
            : undefined,
          terminated_date: associate.terminated_date
            ? moment(associate.terminated_date).utc().startOf('day').format(serverDateFormat)
            : undefined,
          seniority_date: associate.seniority_date
            ? moment(associate.seniority_date).utc().startOf('day').format(serverDateFormat)
            : undefined
        },
        updated_associate_number
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.associate
    } catch (error) {
      throw error.message
    }
  },

  async checkExistingUser(_, { email }) {
    try {
      const { data } = await apiClient.get(`/api/user/email/${email}`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async checkAvailableAssociateNumber({ rootState }, { associate_number }) {
    try {
      const { data } = await apiClient.post('/api/is_associate_number_available', {
        project_id: rootState.applicationState.currentProject.id,
        associate_number
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.result
    } catch (error) {
      throw error.message
    }
  },

  async getCreationContext(_, { associate_id }) {
    try {
      const { data } = await apiClient.get(`/api/associate/${associate_id}/create_context`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async postCloneAssociate(
    { rootState },
    { new_associate_number, associate, disable_previous_associate, is_legal_entity_admin, clone_records }
  ) {
    try {
      const { data } = await apiClient.post('/api/associate/clone', {
        project_id: rootState.applicationState.currentProject.id,
        new_associate_number,
        associate,
        disable_previous_associate,
        is_legal_entity_admin,
        clone_records
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async postTransferAssociateRecords(_, { associate_id }) {
    try {
      const { data } = await apiClient.post(`/api/associate/${associate_id}/records/transfer`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },

  async downloadExportedAssociate(_, { associate_id, filters }) {
    try {
      const { data } = await apiClient.get(`/api/report/associate/${associate_id}/associate_card/download`, {
        params: {
          ...filters
        }
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.url
    } catch (error) {
      throw error.message
    }
  },

  async sendExportedAssociate(_, { associate_id, email, filters }) {
    try {
      const { data } = await apiClient.get(`/api/report/associate/${associate_id}/associate_card/send_email/${email}`, {
        params: {
          ...filters
        }
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
    } catch (error) {
      throw error.message
    }
  },

  setSignedDocuments({ commit }, payload) {
    commit('setSignedDocuments', payload)
  },

  async getSignedDocuments({ commit }, { associate_id, page = 1, size = 10, filter }) {
    try {
      const { data } = await apiClient.post('/api/document/associate/signed/get_all', {
        associate_id,
        page,
        size,
        filter
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while loading the signed documents.'}`)
      }

      commit('setSignedDocuments', data)
    } catch (error) {
      throw error.message
    }
  },

  async synchronizeAssociateShifts(_, { associate_id }) {
    try {
      const { data } = await apiClient.post(`/api/shift/schedule_sync/associate/${associate_id}`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while synchronize shifts.'}`)
      }
    } catch (error) {
      throw error.message
    }
  },

  async sendSetAvailabilityAlert(_, { associate_id }) {
    try {
      const { data } = await apiClient.post(`/api/associate/${associate_id}/send_set_availability_alert`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async postLinkToStuffAssociate(_, { id, type }) {
    try {
      const { data } = await apiClient.post(`/api/associate/${id}/${type}`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while connecting.'}`)
      }

      return data.result
    } catch (error) {
      throw error.message
    }
  },

  async postCreateStuffAssociate(_, { id }) {
    try {
      const { data } = await apiClient.post(`/api/associate/${id}/create_staff_associate`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while creating a profile in stuff company.'}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async postAssociateSendToOnboarding(_, { associate_id }) {
    try {
      const { data } = await apiClient.post(`/api/associate/${associate_id}/send_to_onboarding`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async postAssignAssociateToProject(_, { id, type, rate, associate_ids }) {
    try {
      const { data } = await apiClient.post(`/api/associate/assign_to_project/${id}`, {
        type,
        associate_ids,
        rate
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while assign associates to project.'}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async postAssignAssociateToProjectV2(_, { id, type, rate, associate_ids }) {
    try {
      const { data } = await apiClient.post(`/api/associate/assign_to_project_v2/${id}`, {
        type,
        associate_ids,
        rate
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while creating assignment requests.'}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async getAssignmentRequests(_, { status, page, per_page }) {
    try {
      const { data } = await apiClient.get('/api/associate/assignment_requests', {
        params: {
          status,
          page,
          per_page
        }
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while loading assignment requests.'}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async getAssignmentRequest(_, { request_id }) {
    try {
      const { data } = await apiClient.get(`/api/associate/assignment_requests/${request_id}`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while loading the assignment request.'}`)
      }

      return data.assignment_request
    } catch (error) {
      throw error.message
    }
  },

  async acceptAssignmentRequest(_, { request_id }) {
    try {
      const { data } = await apiClient.post(`/api/associate/assignment_requests/${request_id}/accept`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while accepting the assignment request.'}`)
      }

      return data.assignment_request
    } catch (error) {
      throw error.message
    }
  },

  async rejectAssignmentRequest(_, { request_id, reason }) {
    try {
      const { data } = await apiClient.post(`/api/associate/assignment_requests/${request_id}/reject`, {
        reason
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'An error occurred while rejecting the assignment request.'}`)
      }

      return data.assignment_request
    } catch (error) {
      throw error.message
    }
  },

  async downloadComplianceReport(_, { associate_id }) {
    try {
      const { data } = await apiClient.get('/api/associate/required_documents_print', {
        params: {
          associate_id
        }
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async postRates({ state, commit }, { associate_id, form }) {
    try {
      const { data } = await apiClient.put(`/api/associate/${associate_id}/rate`, {
        ...form
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      const { method, hourly_rate, training_rate, orientation_rate, salary } = data.record

      commit('SET_ASSOCIATE', {
        ...state.associate,
        payment_info: {
          ...state.associate.payment_info,
          method,
          hourly_rate,
          training_rate,
          orientation_rate,
          salary
        }
      })

      return data.record
    } catch (error) {
      throw error.message
    }
  },

  async getRateHistory(_, { associate_id }) {
    try {
      const { data } = await apiClient.get(`/api/associate/${associate_id}/rate/history`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.history_records
    } catch (error) {
      throw error.message
    }
  },

  async rollbackRateHistory(_, { associate_id, history_id, reason }) {
    try {
      const { data } = await apiClient.post(`/api/associate/${associate_id}/rate/history/${history_id}/rollback`, {
        reason
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.record
    } catch (error) {
      const responseData = error.response?.data
      const rollbackError = new Error(responseData?.msg ?? responseData?.detail ?? error.message)

      rollbackError.status = error.response?.status
      rollbackError.data = responseData

      throw rollbackError
    }
  },

  // Shift Penalty
  async getShiftPenalty(_, { associate_id, page, size, filter }) {
    try {
      const { data } = await apiClient.get('/api/associate/shift_penalty', {
        params: { associate_id, page, size, ...filter }
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(data.msg)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async postShiftPenalty(_, { associate_id, amount, reason }) {
    try {
      const { data } = await apiClient.post('/api/associate/shift_penalty', {
        associate_id,
        amount,
        reason
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(data.msg)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async cancelShiftPenalty(_, { id }) {
    try {
      const { data } = await apiClient.delete(`/api/associate/shift_penalty/${id}`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(data.msg)
      }
    } catch (error) {
      throw error.message
    }
  }
}
